import { useQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { Close } from "@mui/icons-material";
import { Launch as DetailsIcon } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { keyBy } from "lodash";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { ReactElement, useEffect, useState } from "react";
import { useForm, useFieldArray, FormProvider, Control } from "react-hook-form";
import {
  useQueryParam,
  withDefault,
  NumberParam,
  StringParam,
} from "use-query-params";

import {
  TargetingForm,
  SavedSearchSchema,
  TargetingFormSchema,
} from "@stw/yup-validators";

import {
  GetSubaccountShortcodeQuery,
  GetAudienceSearchUserFilterQuery,
  AudienceSearchFilterType,
  CreateAudienceSearchFilterMutation,
  CreateAudienceSearchFilterMutationVariables,
  UpdateAudienceSearchFilterMutation,
  UpdateAudienceSearchFilterMutationVariables,
  UpsertAudienceSearchUserFilterMutation,
  UpsertAudienceSearchUserFilterMutationVariables,
  AudienceFieldData,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import Table, {
  TableControls,
  TableColumnAlignment,
  RowAction,
} from "~frontend/components/Table";
import {
  convertTargetingFormToGraphQL,
  DEFAULT_TARGET,
  TargetGroups,
} from "~frontend/components/audienceFilteringForm";
import { RhfTextField } from "~frontend/components/forms/react-hook-form-components";
import {
  CREATE_AUDIENCE_SEARCH_FILTER,
  GET_AUDIENCE_SEARCH_USER_FILTER,
  LIST_AUDIENCE_SEARCH_FILTERS,
  LIST_AUDIENCE_SEARCH_RESULTS,
  UPDATE_AUDIENCE_SEARCH_FILTER,
  UPSERT_AUDIENCE_SEARCH_USER_FILTER,
} from "~frontend/graphql/audience";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import {
  useSubaccount,
  useSubaccountAdminConfig,
} from "~frontend/providers/SubaccountProvider";
import theme, { palette } from "~frontend/theme";
import { formatShortDateTime } from "~frontend/util/dateFormat";
import { useContactNameFormatter } from "~frontend/util/formatContactName";
import { yupPhoneNumber, prettyPhoneNumber } from "~frontend/util/phoneNumber";
import useMutation from "~frontend/util/useMutation";

import { SavedSearches, SavedSearchResultSummary } from "./SavedSearches";
import { useAudience } from "./store";

type SearchResultWithContact = {
  id: string;
  phoneNumber: string;
  fields: AudienceFieldData[];
  createdAt: number;
  updatedAt: number;
};

function AudienceFilters({
  opnId,
  audienceId,
}: {
  opnId: string;
  audienceId: string;
}) {
  const subaccount = useSubaccountAdminConfig()!;
  const timezone = subaccount.textingHoursTimezone;

  const [upsertAudienceSearchUserFilter] = useMutation<
    UpsertAudienceSearchUserFilterMutation,
    UpsertAudienceSearchUserFilterMutationVariables
  >(UPSERT_AUDIENCE_SEARCH_USER_FILTER, {
    onErrorSnackbarMessage: "Failed to apply filters",
  });
  const { enqueueSnackbar } = useSnackbar();

  const currentFilter = useAudience(store => store.currentFilter);
  const isViewingSavedSearch = currentFilter.id !== undefined;
  const isEditingExistingFilter = useAudience(
    store => store.isEditingExistingFilter
  );
  const clearFilters = useAudience(store => store.clearFilters);

  const formMethods = useForm<TargetingForm & { name: string }>({
    resolver: zodResolver(SavedSearchSchema),
    mode: "all",
    reValidateMode: "onBlur",
    defaultValues: isEditingExistingFilter
      ? SavedSearchSchema.parse(currentFilter)
      : currentFilter,
  });

  const { fields, append, remove } = useFieldArray({
    control: formMethods.control,
    name: "targetGroups",
  });

  const [createFilter] = useMutation<
    CreateAudienceSearchFilterMutation,
    CreateAudienceSearchFilterMutationVariables
  >(CREATE_AUDIENCE_SEARCH_FILTER, {
    refetchQueries: [LIST_AUDIENCE_SEARCH_FILTERS],
  });

  const [updateFilter] = useMutation<
    UpdateAudienceSearchFilterMutation,
    UpdateAudienceSearchFilterMutationVariables
  >(UPDATE_AUDIENCE_SEARCH_FILTER, {
    refetchQueries: [LIST_AUDIENCE_SEARCH_FILTERS],
  });

  const [nameModalOpen, setNameModalOpen] = useState<boolean>(false);

  const handleSaveFilter = async () => {
    const { name, targetGroups } = formMethods.getValues();
    if (isViewingSavedSearch) {
      const { errors } = await updateFilter({
        variables: {
          input: {
            audienceId,
            audienceSearchFilterId: currentFilter.id!,
            searchBody: convertTargetingFormToGraphQL({ targetGroups }),
            name,
            type: AudienceSearchFilterType.SavedSearch,
          },
        },
      });

      if (errors) {
        enqueueSnackbar("Failed to save filter", { variant: "error" });
        return;
      }
    } else {
      const { errors } = await createFilter({
        variables: {
          input: {
            subaccountId: subaccount.id,
            audienceId,
            searchBody: convertTargetingFormToGraphQL({ targetGroups })!,
            name,
            type: AudienceSearchFilterType.SavedSearch,
          },
        },
      });

      if (errors) {
        enqueueSnackbar("Failed to create filter", { variant: "error" });
        return;
      }
    }

    clearFilters();
    setNameModalOpen(false);
    enqueueSnackbar("Filter saved successfully", { variant: "success" });
  };

  async function applyFilters() {
    const data = formMethods.getValues();

    await upsertAudienceSearchUserFilter({
      variables: {
        input: {
          subaccountId: subaccount.id,
          audienceId,
          searchBody: convertTargetingFormToGraphQL(data)!,
          recalculate: true,
        },
      },
      awaitRefetchQueries: true,
      refetchQueries: [GET_AUDIENCE_SEARCH_USER_FILTER],
    });
  }

  return (
    <>
      {isViewingSavedSearch && <SavedSearchResultSummary />}

      {(!isViewingSavedSearch || isEditingExistingFilter) && (
        <FormProvider {...formMethods}>
          <Dialog open={nameModalOpen} onClose={() => setNameModalOpen(false)}>
            <DialogTitle>Name and Save Search</DialogTitle>
            <DialogContent>
              <RhfTextField name="name" label="Saved Search Name" fullWidth />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setNameModalOpen(false)}>Cancel</Button>
              <Button
                type="submit"
                onClick={formMethods.handleSubmit(handleSaveFilter)}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>
          <FlexBox
            direction="column"
            sx={{
              backgroundColor: palette.lightGray,
              padding: theme.spacing(3),
            }}
          >
            <TargetGroups
              control={formMethods.control as unknown as Control<TargetingForm>}
              opnId={opnId}
              timezone={timezone}
              targetGroups={fields}
              removeGroup={remove}
              variant="slim"
            />
            <FlexBox gap={2} justifyContent="flex-end">
              <Button
                color="secondary"
                variant="contained"
                sx={{ fontSize: "10px" }}
                onClick={() => append(DEFAULT_TARGET)}
              >
                + Add Filter Group
              </Button>
              <Button
                variant="contained"
                color="primary"
                sx={{ fontSize: "10px" }}
                onClick={applyFilters}
              >
                Apply Filters
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                sx={{ fontSize: "10px" }}
                onClick={() => setNameModalOpen(true)}
              >
                Save Filter (temp)
              </Button>
            </FlexBox>
          </FlexBox>
        </FormProvider>
      )}
    </>
  );
}

export function AudienceSubscribersTable({
  shortcode,
  userSearchFilter,
}: {
  shortcode: NonNullable<
    GetSubaccountShortcodeQuery["getSubaccount"]["shortcode"]
  >;
  userSearchFilter?: GetAudienceSearchUserFilterQuery["getAudienceSearchUserFilter"];
}): ReactElement {
  const { subaccount } = useSubaccount()!;
  const formatSubscriberName = useContactNameFormatter();
  const customFieldsAllowed = useFeatureFlag("audienceCustomFields");

  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 0));
  const [pageSize, setPageSize] = useQueryParam(
    "page_size",
    withDefault(NumberParam, 10)
  );
  // TODO: implement sorting
  // const [sort, setSort] = useQueryParam(
  //   "sort",
  //   withDefault(StringParam, "created_desc")
  // );

  const [search, setSearch] = useQueryParam(
    "search",
    withDefault(StringParam, "")
  );
  const activeAudienceSearchFilter = useAudience(
    store => store.activeAudienceSearchFilter
  );
  const setActiveAudienceSearchFilter = useAudience(
    store => store.setActiveAudienceSearchFilter
  );

  const { data, loading, error, refetch } = useQuery(
    LIST_AUDIENCE_SEARCH_RESULTS,
    {
      variables: {
        input: {
          subaccountId: subaccount.id,
          audienceId: shortcode.subscriberAudienceId!,
          audienceSearchFilterId: activeAudienceSearchFilter?.id,
          search: search,
          limit: pageSize,
        },
      },
      skip: activeAudienceSearchFilter === null,
    }
  );

  useEffect(() => {
    void refetch();
  }, [activeAudienceSearchFilter, refetch]);

  const [activeStatusFilter, setActiveStatusFilter] = useState<boolean | null>(
    null
  );
  const setFilter = useAudience(store => store.setFilter);
  const isFilterEmpty = useAudience(store => store.isFilterEmpty());
  const clearFilters = useAudience(store => store.clearFilters);
  const isEditingExistingFilter = useAudience(
    store => store.isEditingExistingFilter
  );

  return (
    <>
      <TableControls
        filters={[
          {
            id: "active-status",
            label: "Subscription Status",
            onChange: (newStatusFilter: string) => {
              if (newStatusFilter === "active") {
                setActiveStatusFilter(true);
              } else if (newStatusFilter === "unsubscribed") {
                setActiveStatusFilter(false);
              } else if (newStatusFilter === "all") {
                setActiveStatusFilter(null);
              }
            },
            options: [
              {
                value: "all",
                label: "All",
              },
              {
                value: "active",
                label: "Subscribed",
              },
              {
                value: "unsubscribed",
                label: "Unsubscribed",
              },
            ],
            value:
              activeStatusFilter === true
                ? "active"
                : activeStatusFilter === false
                  ? "unsubscribed"
                  : "all",
          },
        ]}
        search={{
          onChange: value => {
            setSearch(value as string);
            setPage(0);
          },
          placeholder: "Search by phone number",
          value: search,
          schema: yupPhoneNumber,
        }}
      />

      <FlexBox direction="column">
        {!isEditingExistingFilter && (
          <FlexBox
            justifyContent="flex-end"
            sx={{ marginBottom: theme.spacing(2) }}
          >
            <Button
              variant={isFilterEmpty ? "contained" : "outlined"}
              color={isFilterEmpty ? "primary" : "secondary"}
              sx={{ fontSize: "10px" }}
              onClick={() => {
                setActiveAudienceSearchFilter(userSearchFilter);
                if (isFilterEmpty) {
                  if (userSearchFilter) {
                    setFilter(
                      TargetingFormSchema.parse(userSearchFilter.searchBody)
                    );
                  } else {
                    setFilter({ targetGroups: [DEFAULT_TARGET] });
                  }
                } else {
                  clearFilters();
                }
              }}
            >
              {isFilterEmpty ? (
                "Filter Contacts"
              ) : (
                <>
                  <Close sx={{ fontSize: "12px", mr: 1 }} />
                  Clear Filters
                </>
              )}
            </Button>
          </FlexBox>
        )}

        {!isFilterEmpty && (
          <AudienceFilters
            opnId={shortcode.id}
            audienceId={shortcode.subscriberAudienceId!}
          />
        )}

        <SavedSearches audienceId={shortcode.subscriberAudienceId!} />

        <AsyncWrapper loading={loading} errors={error} data={data}>
          {searchResults => {
            if (searchResults.listAudienceSearchResults.totalCount === 0) {
              return (
                <FlexBox
                  alignItems="center"
                  justifyContent="center"
                  sx={{ py: 10, px: 5 }}
                >
                  <Typography variant="body2">
                    No contacts met your filter criteria.{" "}
                  </Typography>
                  <Button variant="link">
                    <Typography variant="body2">Clear filters</Typography>
                  </Button>
                </FlexBox>
              );
            }

            return (
              <FlexBox sx={{ px: 5, py: 3, backgroundColor: palette.white }}>
                <Table
                  totalCount={
                    searchResults.listAudienceSearchResults.totalCount
                  }
                  rows={
                    searchResults.listAudienceSearchResults
                      .audienceSearchResults
                  }
                  columns={[
                    {
                      key: "phoneNumber",
                      title: "Number",
                      align: "left",
                      renderValue: (searchResult: SearchResultWithContact) =>
                        searchResult.phoneNumber
                          ? prettyPhoneNumber(searchResult.phoneNumber)
                          : "",
                    },
                    {
                      key: "name",
                      title: "Name",
                      align: "left",
                      renderValue: (searchResult: SearchResultWithContact) =>
                        formatSubscriberName(
                          {
                            firstName: searchResult.fields?.find(
                              f => f.name === "first_name"
                            )?.value,
                            lastName: searchResult.fields?.find(
                              f => f.name === "last_name"
                            )?.value,
                          },
                          {
                            overrideNoNameText: "-",
                          }
                        ),
                    },
                    {
                      key: "address",
                      title: "Address",
                      align: "left",
                      renderValue: searchResult => {
                        const fields = keyBy(searchResult.fields, "name") || {};
                        return (
                          <>
                            {fields.address_line_1?.value}
                            {fields.address_line_1?.value &&
                              fields.address_line_2?.value &&
                              ", "}
                            {fields.address_line_2?.value}
                            {(fields.address_line_1?.value ||
                              fields.address_line_2?.value) &&
                              (fields.city?.value ||
                                fields.state?.value ||
                                fields.zip?.value) && <br />}
                            {[fields.city?.value, fields.state?.value]
                              .filter(Boolean)
                              .join(", ")}{" "}
                            {fields.zip.value}
                          </>
                        );
                      },
                    },
                    {
                      key: "added",
                      title: "Added",
                      align: "left",
                      renderValue: searchResult => {
                        const formattedCreatedAt = formatShortDateTime(
                          DateTime.fromMillis(searchResult.createdAt)
                        );
                        const formattedUpdatedAt = formatShortDateTime(
                          DateTime.fromMillis(searchResult.updatedAt)
                        );

                        return (
                          <>
                            {formattedCreatedAt}
                            {formattedUpdatedAt !== formattedCreatedAt && (
                              <Typography display="inline" variant="body3">
                                <br />
                                Updated {formattedUpdatedAt}
                              </Typography>
                            )}
                          </>
                        );
                      },
                    },
                    ...(customFieldsAllowed
                      ? [
                          {
                            key: "customFields",
                            title: "Custom Fields",
                            align: "left" as TableColumnAlignment,
                            sx: { maxWidth: "450px" },
                            renderValue: (
                              searchResult: SearchResultWithContact
                            ) => {
                              // only built-in fields have displayName
                              const customFields = (
                                searchResult.fields || []
                              ).filter(f => !f.displayName);
                              if (customFields.length === 0) {
                                return <div />;
                              }
                              return customFields.map(field => (
                                <div key={field.name}>
                                  {field.name}
                                  {field.type === "BOOLEAN" ? "?" : ""}:{" "}
                                  {typeof field.value === "boolean" &&
                                  field.value !== undefined
                                    ? field.value
                                      ? "True"
                                      : "False"
                                    : field.value}
                                </div>
                              ));
                            },
                          },
                        ]
                      : []),
                  ]}
                  rowActions={_searchResult => {
                    const actions: RowAction<SearchResultWithContact>[] = [];

                    actions.push({
                      id: "details",
                      tooltipLabel: "Details",
                      icon: <DetailsIcon />,
                      onClick: () => {}, // TODO: implement setDialogOpenFor(subscriber),
                    });

                    return actions;
                  }}
                  rowsPerPage={pageSize}
                  page={page}
                  onChangeRowsPerPage={setPageSize}
                  onChangePage={setPage}
                />
              </FlexBox>
            );
          }}
        </AsyncWrapper>
      </FlexBox>
    </>
  );
}
